import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useReservaFilters } from '../../hooks/useReservaFilters';
import BotonRuta from '../../components/shared/BotonRuta';
import { getEstadoColor, getSubEstadoColor } from '../../constants/reservaColors';
import { createReservaIcs, downloadIcsFile } from '../../utils/ics';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  Select,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';

const ConductorServicios = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [reservasOriginal, setReservasOriginal] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [isModalSocioOpen, setIsModalSocioOpen] = useState(false);
  const [conductoresHabilitados, setConductoresHabilitados] = useState([]);
  const [conductorSeleccionado, setConductorSeleccionado] = useState('');
  const [cargandoConductores, setCargandoConductores] = useState(false);
  const [isModalInvitarOpen, setIsModalInvitarOpen] = useState(false);
  const [emailInvitado, setEmailInvitado] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isAbandonando, setIsAbandonando] = useState(false);
  const [isTransfiriendo, setIsTransfiriendo] = useState(false);
  const [isInvitando, setIsInvitando] = useState(false);

  const handleDescargarIcs = (reserva) => {
    const icsData = createReservaIcs(reserva);
    if (!icsData) {
      toast({
        title: 'No se puede generar el calendario',
        description: 'La reserva no tiene fecha de inicio válida.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    downloadIcsFile(icsData.content, icsData.filename);
  };

  // Hook de filtros con vinculación por color
  const {
    estadosActivos,
    subEstadosActivos,
    toggleEstado,
    toggleSubEstado
  } = useReservaFilters(
    {
      'activo': true,
      'inactivo': false,
      'cancelado': false
    },
    {
      'pendiente_pago': false,
      'bolsa': false,
      'asignada_espera': true,
      'en_curso': true,
      'completado_sin_confirmar': false,
      'finalizado': false,
      'cancelado_cliente': false,
      'cancelado_administrador': false,
      'cancelado_automatico': false,
      'fallido': false
    }
  );

  const columnsToDisplay = [
    'acciones',
    'estado',
    'sub_estado',
    'idioma',
    'tipo',
    'fecha_hora_inicio',
    'fecha_hora_fin',
    'cliente',
    'origen_direccion',
    'destino_direccion',
    'precio',
    'distancia_km',
    'created_at',
  ];

  // useEffect separado para manejar el toast de adjudicación exitosa (solo se ejecuta una vez)
  useEffect(() => {
    const estado = searchParams.get('estado');
    const tipo = searchParams.get('tipo');
    
    if (estado === 'ok' && tipo === 'adjudicacion') {
      console.log('[ADJUDICACION] Mostrando toast de éxito');
      toast({
        title: '¡Servicio adjudicado!',
        description: 'El servicio ha sido asignado correctamente.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      // Limpiar parámetros de la URL inmediatamente
      navigate('/conductor/servicio-activo', { replace: true });
    }
  }, [navigate, searchParams, toast]);

  useEffect(() => {
    const fetchReservas = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const token = localStorage.getItem('auth-token');
        
        const response = await fetch(`${baseURL}/api/reservas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            `Error fetching reservas: ${response.status} - ${
              errorData?.detail || response.statusText
            }`
          );
        }
        const data = await response.json();
        
        // Filtrar solo las reservas del conductor actual
        // IMPORTANTE: obtener user.id directamente desde localStorage para evitar problemas de timing
        const storedUser = localStorage.getItem('auth-user');
        if (!storedUser) {
          console.warn('[FILTRO] No hay usuario en localStorage');
          setReservasOriginal([]);
          setReservas([]);
          setLoading(false);
          return;
        }
        
        const userData = JSON.parse(storedUser);
        const userId = userData?.user_id;
        
        if (!userId) {
          console.warn('[FILTRO] No hay user_id en localStorage:', userData);
          setReservasOriginal([]);
          setReservas([]);
          setLoading(false);
          return;
        }

        console.log('[FILTRO] Filtrando reservas para conductor:', userId);
        console.log('[FILTRO] Total reservas recibidas:', data.length);
        
        // Debug: mostrar conductor_id de las primeras 3 reservas
        data.slice(0, 3).forEach((r, i) => {
          console.log(`[FILTRO] Reserva ${i}: conductor_id=${r.conductor_id}, user_id=${userId}`);
        });
        
        const reservasConductor = data.filter(reserva => {
          // Asegurar comparación correcta (ambos como strings)
          const conductorId = String(reserva.conductor_id || '');
          const userIdStr = String(userId);
          const match = conductorId === userIdStr;
          if (match) {
            console.log(`[FILTRO] ✅ Match encontrado: reserva ${reserva.id}`);
          }
          return match;
        });
        
        console.log(`[FILTRO] ✅ Total: ${data.length} reservas, Filtradas: ${reservasConductor.length} para conductor ${userId}`);
        
        setReservasOriginal(reservasConductor);
        setReservas(reservasConductor);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReservas();
  }, []); // Ejecutar solo una vez al montar, sin depender de user

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Aplicar filtros primero
    let reservasFiltradas = reservasOriginal.filter(reserva => {
      const estadoActivo = estadosActivos[reserva.estado];
      const subEstadoActivo = subEstadosActivos[reserva.sub_estado];
      return estadoActivo && subEstadoActivo;
    });

    // Luego aplicar ordenamiento
    if (sortBy) {
      const sortedReservas = [...reservasFiltradas].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortOrder === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
          return sortOrder === 'asc'
            ? (valueA === valueB ? 0 : (valueA ? 1 : -1))
            : (valueA === valueB ? 0 : (valueA ? -1 : 1));
        } else {
          const stringA = String(valueA);
          const stringB = String(valueB);
          return sortOrder === 'asc'
            ? stringA.localeCompare(stringB)
            : stringB.localeCompare(stringA);
        }
      });
      setReservas(sortedReservas);
    } else {
      setReservas(reservasFiltradas);
    }
  }, [sortBy, sortOrder, reservasOriginal, estadosActivos, subEstadosActivos]);

  const handleCancelar = (reserva) => {
    setSelectedReserva(reserva);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReserva(null);
  };

  const handleTransferirServicio = async () => {
    // NO limpiar selectedReserva aquí, solo cerrar el modal de opciones
    setIsModalOpen(false);
    setCargandoConductores(true);
    setIsModalSocioOpen(true);
    
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/conductores/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar conductores');
      }
      
      const data = await response.json();
      // Filtrar solo conductores habilitados y excluir al conductor actual
      const habilitados = data.filter(c => c.habilitado === true && c.id !== user?.user_id);
      setConductoresHabilitados(habilitados);
      setConductorSeleccionado('');
    } catch (error) {
      console.error('Error al cargar conductores:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los conductores',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsModalSocioOpen(false);
    } finally {
      setCargandoConductores(false);
    }
  };

  const handleConfirmarTransferencia = async () => {
    if (!selectedReserva || !conductorSeleccionado) return;

    setIsTransfiriendo(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');

      // Usar el endpoint específico de transferencia que envía notificaciones
      const response = await fetch(`${baseURL}/api/reservas/${selectedReserva.id}/transferir-conductor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conductor_id: conductorSeleccionado
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al transferir el servicio');
      }

      toast({
        title: 'Servicio transferido',
        description: 'El servicio ha sido transferido correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setIsModalSocioOpen(false);
      setSelectedReserva(null); // Limpiar la reserva seleccionada después de transferir

      // Eliminar la reserva de la lista local (ya no está asignada a este conductor)
      const reservasActualizadas = reservasOriginal.filter(r => r.id !== selectedReserva.id);
      setReservasOriginal(reservasActualizadas);
    } catch (error) {
      console.error('Error al transferir:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo transferir el servicio',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTransfiriendo(false);
    }
  };

  const handleInvitarConductor = () => {
    // Cerrar el modal de opciones y abrir el modal de invitación
    setIsModalOpen(false);
    setEmailInvitado('');
    setEmailError('');
    setIsModalInvitarOpen(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirmarInvitacion = async () => {
    // Validar email
    if (!emailInvitado) {
      setEmailError('El email es requerido');
      return;
    }
    if (!validateEmail(emailInvitado)) {
      setEmailError('Email inválido');
      return;
    }

    if (!selectedReserva) return;

    setIsInvitando(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');

      const response = await fetch(`${baseURL}/api/reservas/${selectedReserva.id}/invitar-conductor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_invitado: emailInvitado
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al enviar la invitación');
      }

      toast({
        title: 'Invitación enviada',
        description: `Se ha enviado una invitación a ${emailInvitado}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setIsModalInvitarOpen(false);
      setSelectedReserva(null);

      // Eliminar la reserva de la lista local (ya no está asignada a este conductor)
      const reservasActualizadas = reservasOriginal.filter(r => r.id !== selectedReserva.id);
      setReservasOriginal(reservasActualizadas);
    } catch (error) {
      console.error('Error al invitar:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar la invitación',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsInvitando(false);
    }
  };

  const handleConfirmarCancelacion = async () => {
    if (!selectedReserva) return;

    setIsAbandonando(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');

      const response = await fetch(`${baseURL}/api/reservas/${selectedReserva.id}/cancelar-conductor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al abandonar el servicio');
      }

      toast({
        title: 'Servicio abandonado',
        description: 'Has abandonado el servicio correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      handleCloseModal();

      // Eliminar la reserva de la lista local (ya no está asignada al conductor)
      const reservasActualizadas = reservasOriginal.filter(r => r.id !== selectedReserva.id);
      setReservasOriginal(reservasActualizadas);
    } catch (error) {
      console.error('Error al abandonar:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo abandonar el servicio',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAbandonando(false);
    }
  };

  const renderCellValue = (reserva, key) => {
    const value = reserva[key];
    
    // Renderizar columna de acciones (botón de ruta + botón de abandonar)
    if (key === 'acciones') {
      const isDisabled = reserva.estado !== 'activo';
      return (
        <VStack spacing={1} align="stretch">
          <BotonRuta
            origen={reserva.origen}
            destino={reserva.destino}
            size="sm"
            fullWidth={true}
            colorScheme="teal"
            variant="outline"
          />
          <Button
            size="sm"
            width="full"
            className="boton-ver-datos"
            onClick={(e) => {
              e.stopPropagation();
              handleDescargarIcs(reserva);
            }}
            isDisabled={!reserva.fecha_hora_inicio}
          >
            📅 Añadir a calendario
          </Button>
          <IconButton
            icon={<span style={{ fontSize: '18px' }}>❌</span>}
            className="boton-cancelar-devolver"
            size="sm"
            width="full"
            isDisabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              handleCancelar(reserva);
            }}
            title={isDisabled ? 'Solo se pueden abandonar servicios activos' : 'Abandonar servicio'}
          />
        </VStack>
      );
    }

    // Renderizar email del cliente
    if (key === 'cliente') {
      return (
        <Text fontSize="sm">{reserva.cliente_email || 'N/A'}</Text>
      );
    }

    // Renderizar direcciones con fallback a código postal
    if (key === 'origen_direccion') {
      const direccion = reserva.origen_direccion || reserva.origen_codigo_postal || 'N/A';
      return <Text fontSize="sm">{direccion}</Text>;
    }

    if (key === 'destino_direccion') {
      const direccion = reserva.destino_direccion || reserva.destino_codigo_postal || 'N/A';
      return <Text fontSize="sm">{direccion}</Text>;
    }

    // Renderizar idioma con icono de bandera (ANTES de la verificación de null/undefined)
    if (key === 'idioma') {
      const idiomaLower = value ? value.toLowerCase() : 'es';
      const iconosDisponibles = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca'];
      const iconoPath = iconosDisponibles.includes(idiomaLower) 
        ? `/assets/icons/${idiomaLower}.png` 
        : '/assets/icons/es.png';
      
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <img 
            src={iconoPath} 
            alt={idiomaLower} 
            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
          />
        </Box>
      );
    }
    
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Renderizar estado con badge de color
    if (key === 'estado') {
      const color = getEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`estado_${value}`) || value.toUpperCase()}
        </Badge>
      );
    }

    // Renderizar sub_estado con badge de color
    if (key === 'sub_estado') {
      const color = getSubEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`subestado_${value}`) || value.toUpperCase().replace(/_/g, ' ')}
        </Badge>
      );
    }

    // Formatear fechas
    if (key.includes('fecha') || key.includes('created_at') || key.includes('updated_at')) {
      try {
        const date = new Date(value);
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return String(value);
      }
    }

    // Formatear números decimales
    if (typeof value === 'number') {
      return value.toFixed(2);
    }

    return String(value);
  };

  const formatColumnHeader = (key) => {
    return t(`col_${key}`) || key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="xl" />
        <Text ml={4}>{t('conductor_cargando_servicios')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" overflow="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold">
          {t('conductor_titulo_mis_servicios')}
        </Text>
        <Button 
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          colorScheme="blue"
          size="sm"
          leftIcon={<span>🔍</span>}
          className="boton-filtros"
        >
          {t('filtros_boton')}
        </Button>
      </Box>

      {/* Leyenda de colores - Filtros interactivos */}
      {mostrarFiltros && (
      <Box className="filter-section-bg">
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="black">{t('filtros_estados_titulo')}</Text>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={estadosActivos['activo'] ? '2px solid white' : '2px solid transparent'}
            opacity={estadosActivos['activo'] ? 1 : 0.4}
            onClick={() => toggleEstado('activo')}
          >
            {t('estado_activo')}
          </Badge>
          <Badge 
            colorScheme="yellow" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={estadosActivos['inactivo'] ? '2px solid white' : '2px solid transparent'}
            opacity={estadosActivos['inactivo'] ? 1 : 0.4}
            onClick={() => toggleEstado('inactivo')}
          >
            {t('estado_inactivo')}
          </Badge>
          <Badge 
            colorScheme="red" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={estadosActivos['cancelado'] ? '2px solid white' : '2px solid transparent'}
            opacity={estadosActivos['cancelado'] ? 1 : 0.4}
            onClick={() => toggleEstado('cancelado')}
          >
            {t('estado_cancelado')}
          </Badge>
        </Box>
        
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="white">{t('filtros_subestados_titulo')}</Text>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['bolsa'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['bolsa'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('bolsa')}
          >
            {t('subestado_bolsa')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['asignada_espera'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['asignada_espera'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('asignada_espera')}
          >
            {t('subestado_asignada_espera')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['en_curso'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['en_curso'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('en_curso')}
          >
            {t('subestado_en_curso')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['completado_sin_confirmar'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['completado_sin_confirmar'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('completado_sin_confirmar')}
          >
            {t('subestado_completado_sin_confirmar')}
          </Badge>
          <Badge 
            colorScheme="yellow" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['pendiente_pago'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['pendiente_pago'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('pendiente_pago')}
          >
            {t('subestado_pendiente_pago')}
          </Badge>
          <Badge 
            colorScheme="red" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['cancelado_cliente'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['cancelado_cliente'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('cancelado_cliente')}
          >
            {t('subestado_cancelado_cliente')}
          </Badge>
          <Badge 
            colorScheme="red" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['cancelado_administrador'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['cancelado_administrador'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('cancelado_administrador')}
          >
            {t('subestado_cancelado_conductor')}
          </Badge>
        </Box>
      </Box>
      )}

      {reservas.length > 0 ? (
        <Table variant="simple" width="100%" minWidth="1200px">
          <Thead>
            <Tr>
              {columnsToDisplay.map((key) => (
                <Th
                  key={key}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                >
                  {formatColumnHeader(key)}
                  {sortBy === key && (sortOrder === 'asc' ? ' ⬆️' : ' ⬇️')}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {reservas.map((reserva) => (
              <Tr
                key={reserva.id}
                _hover={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', cursor: 'pointer' }}
              >
                {columnsToDisplay.map((key) => (
                  <Td key={`${reserva.id}-${key}`}>
                    {renderCellValue(reserva, key)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text>{t('conductor_sin_servicios')}</Text>
      )}

      {/* Modal de opciones de abandono */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered>
        <ModalOverlay />
        <ModalContent border="2px solid white">
          <ModalHeader>{t('conductor_abandonar_titulo') || 'Abandonar Servicio'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              {t('conductor_abandonar_mensaje') || '¿Qué acción deseas realizar con este servicio?'}
            </Text>
            <VStack spacing={3} width="100%">
              <Button
                width="100%"
                colorScheme="blue"
                onClick={handleTransferirServicio}
                isDisabled={isTransfiriendo || isInvitando || isAbandonando}
              >
                {t('conductor_transferir_servicio') || 'Transferir servicio a otro socio'}
              </Button>
              <Button
                width="100%"
                colorScheme="green"
                onClick={handleInvitarConductor}
                isDisabled={isTransfiriendo || isInvitando || isAbandonando}
              >
                {t('conductor_invitar_conductor') || 'Invitar a un nuevo conductor'}
              </Button>
              <Button
                width="100%"
                colorScheme="red"
                onClick={handleConfirmarCancelacion}
                isLoading={isAbandonando}
                loadingText="Abandonando..."
                isDisabled={isTransfiriendo || isInvitando}
              >
                {t('conductor_confirmar_abandono') || 'Confirmar abandono'}
              </Button>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleCloseModal}>
              {t('conductor_cerrar') || 'Cerrar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de selección de socio */}
      <Modal isOpen={isModalSocioOpen} onClose={() => setIsModalSocioOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent border="2px solid white">
          <ModalHeader>Seleccione socio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {cargandoConductores ? (
              <Box display="flex" justifyContent="center" p={4}>
                <Spinner />
              </Box>
            ) : (
              <Select
                placeholder="Seleccione un conductor..."
                value={conductorSeleccionado}
                onChange={(e) => setConductorSeleccionado(e.target.value)}
              >
                {conductoresHabilitados.map((conductor) => (
                  <option key={conductor.id} value={conductor.id}>
                    Licencia {conductor.licencia_conducir}, {conductor.nombre} {conductor.apellidos}
                  </option>
                ))}
              </Select>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleConfirmarTransferencia}
              isLoading={isTransfiriendo}
              loadingText="Transfiriendo..."
              isDisabled={!conductorSeleccionado}
              mr={3}
            >
              Transferir
            </Button>
            <Button variant="ghost" onClick={() => setIsModalSocioOpen(false)} isDisabled={isTransfiriendo}>
              {t('conductor_cerrar') || 'Cerrar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de invitación a conductor */}
      <Modal isOpen={isModalInvitarOpen} onClose={() => setIsModalInvitarOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent border="2px solid white">
          <ModalHeader>Proporcione el email del conductor</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={!!emailError}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                placeholder="conductor@ejemplo.com"
                value={emailInvitado}
                onChange={(e) => {
                  setEmailInvitado(e.target.value);
                  setEmailError(''); // Limpiar error al escribir
                }}
              />
              <FormErrorMessage>{emailError}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleConfirmarInvitacion}
              isLoading={isInvitando}
              loadingText="Invitando..."
              mr={3}
            >
              Invitar
            </Button>
            <Button variant="ghost" onClick={() => setIsModalInvitarOpen(false)} isDisabled={isInvitando}>
              {t('conductor_cerrar') || 'Cerrar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ConductorServicios;
